import { Button, Selector, Switch } from "@somethings-ui/web";
import { MapPinned, Pencil, Save } from "lucide-react";
import { useState } from "react";
import type { MentorScheduleGroup } from "@/lib/api/mentor-schedule";
import {
  formatHoursInZone,
  formatPodHours,
  localDayTag,
  timeChoices,
  weekForEditing,
  weekFromEditing,
  zoneAbbreviation,
  zoneName,
  type EditableDay,
  type ScheduleDay,
} from "@/lib/schedule/schedule-display";
import { scrollSelectedTime, TIME_MENU_CLASS } from "./time-select";

const DAY_TIMES = timeChoices(false);

const TIME_FIELD_CLASS =
  "h-10 min-w-[7.5rem] justify-between rounded-xl border border-buttonBorder-tertiary bg-surfaces-z0 px-3 font-body text-body-2";

function ZoneChip({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-buttonBorder-tertiary px-1.5 py-0.5 font-body text-body-4 text-text-secondary">
      {children}
    </span>
  );
}

interface ActiveHoursCardProps {
  savedTimezone: string;
  timezone: string;
  fromDevice: boolean;
  hours: ScheduleDay[];
  groups: MentorScheduleGroup[];
  disabled?: boolean;
  pending?: boolean;
  onSaveHours: (hours: ScheduleDay[], timezone: string) => Promise<void>;
}

export function ActiveHoursCard({
  savedTimezone,
  timezone,
  fromDevice,
  hours,
  groups,
  disabled,
  pending,
  onSaveHours,
}: ActiveHoursCardProps) {
  const [draft, setDraft] = useState<EditableDay[] | null>(null);
  const [clash, setClash] = useState<string | null>(null);
  const [groupId, setGroupId] = useState(groups[0]?.id ?? "");
  const zoneAbbr = zoneAbbreviation(timezone);
  const selected =
    groups.find((group) => group.id === groupId) ?? groups[0] ?? null;
  const groupAbbr = selected ? zoneAbbreviation(selected.timezone) : "";

  const updateDay = (day: string, patch: Partial<EditableDay>) => {
    setClash(null);
    setDraft(
      (current) =>
        current?.map((item) =>
          item.day === day ? { ...item, ...patch } : item,
        ) ?? null,
    );
  };

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 p-6">
      <div>
        <h2 className="font-heading text-header-5 text-text-primary">
          Active hours
        </h2>
        <p className="mt-1 font-body text-body-2 text-text-secondary">
          Set your hours in your current timezone. If you change timezones,
          these hours convert. The right side shows when teens in each state are
          online, in their local time.
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[3fr_2fr] lg:grid-rows-[auto_1fr_auto]">
        <div className="flex flex-col gap-4 rounded-2xl bg-surfaces-z2 p-4 lg:row-span-3 lg:grid lg:grid-rows-subgrid">
          <div className="flex flex-col gap-2">
            <p className="font-body text-body-4 uppercase tracking-wide text-text-tertiary">
              Your timezone
            </p>
            <p className="flex items-center gap-2 font-heading text-header-3 text-text-primary">
              {zoneName(timezone)}
              <span className="inline-flex items-center rounded-md bg-brand-trueBlack px-2 py-1 font-body text-body-3 font-medium text-brand-trueWhite">
                {zoneAbbr}
              </span>
            </p>
            {fromDevice && (
              <p className="flex items-center gap-1 font-body text-body-3 text-text-secondary">
                <MapPinned className="size-3" />
                Detected from this device.
              </p>
            )}
          </div>
          <div className="flex flex-col">
            {hours.map((day, index) => {
              const edited = draft?.[index];
              return (
                <div
                  key={day.day}
                  className="grid grid-cols-[7rem_1fr_auto] items-center gap-2 border-t border-buttonBorder-tertiary py-3"
                >
                  <span className="font-body text-body-2 text-text-primary">
                    {day.day}
                  </span>
                  {edited ? (
                    <EditDay
                      day={edited}
                      disabled={disabled}
                      onChange={(patch) => updateDay(day.day, patch)}
                    />
                  ) : (
                    <span className="font-body text-body-2 text-text-primary">
                      {formatHoursInZone(day, savedTimezone, timezone)}
                    </span>
                  )}
                  {(edited ?? day).isOnline && <ZoneChip>{zoneAbbr}</ZoneChip>}
                </div>
              );
            })}
          </div>
          {clash && (
            <p role="alert" className="text-error-brand font-body text-body-3">
              Two shifts would start on {clash}. Change one of them so each day
              has one shift.
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <p className="font-body text-body-3 text-text-tertiary">
              All times in {zoneAbbr}.
            </p>
            {draft ? (
              <div className="flex gap-2">
                <Button
                  fullWidth={false}
                  variant="quaternary"
                  size="sm"
                  type="button"
                  onPress={() => {
                    setClash(null);
                    setDraft(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth={false}
                  variant="primary"
                  size="sm"
                  loading={pending}
                  leadingIcon={<Save className="size-4" />}
                  onPress={async () => {
                    const week = weekFromEditing(
                      draft,
                      timezone,
                      savedTimezone,
                    );
                    if ("clash" in week) {
                      setClash(week.clash);
                      return;
                    }
                    await onSaveHours(week.hours, week.timezone);
                    setDraft(null);
                  }}
                >
                  Save
                </Button>
              </div>
            ) : (
              !disabled && (
                <Button
                  fullWidth={false}
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Pencil className="size-4" />}
                  onPress={() =>
                    setDraft(weekForEditing(hours, savedTimezone, timezone))
                  }
                >
                  Edit hours
                </Button>
              )
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border border-buttonBorder-tertiary p-4 opacity-80 lg:row-span-3 lg:grid lg:grid-rows-subgrid">
          <div className="flex flex-col gap-2">
            <p className="font-body text-body-4 uppercase tracking-wide text-text-tertiary">
              When teens are online
            </p>
            {groups.length > 0 ? (
              <Selector
                aria-label="Billable group"
                options={groups.map((group) => ({
                  value: group.id,
                  label: group.name,
                }))}
                value={selected?.id ?? ""}
                onChange={setGroupId}
                triggerClassName="h-14 w-full justify-between rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 px-4 font-body text-body-2"
              />
            ) : (
              <p className="font-body text-body-2 text-text-secondary">
                No billable group yet.
              </p>
            )}
            {selected && (
              <p className="font-body text-body-3 text-text-secondary">
                When teens in this state are online, in their local time (
                {groupAbbr}).
              </p>
            )}
          </div>
          <div className="flex flex-col">
            {(selected?.hours ?? []).map((day) => (
              <div
                key={day.day}
                className="grid grid-cols-[7rem_1fr_auto] items-center gap-2 border-t border-buttonBorder-tertiary py-3"
              >
                <span className="font-body text-body-2 text-text-secondary">
                  {day.day}
                </span>
                <span className="font-body text-body-2 text-text-secondary">
                  {formatPodHours(day)}
                </span>
                {day.isOnline && <ZoneChip>{groupAbbr}</ZoneChip>}
              </div>
            ))}
          </div>
          <p className="font-body text-body-3 text-text-tertiary">
            This side stays on the group contract. Editing your hours does not
            move it.
          </p>
        </div>
      </div>
    </section>
  );
}

function EditDay({
  day,
  disabled,
  onChange,
}: {
  day: EditableDay;
  disabled?: boolean;
  onChange: (patch: Partial<EditableDay>) => void;
}) {
  const startTag = localDayTag(day, "start");
  const endTag = localDayTag(day, "end");
  if (!day.isOnline) {
    return (
      <div className="flex items-center gap-2">
        <Switch
          aria-label={`${day.day} online`}
          value={false}
          disabled={disabled}
          onValueChange={(next) => onChange({ isOnline: next })}
        />
        <span className="font-body text-body-2 text-text-secondary">Off</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Switch
        aria-label={`${day.day} online`}
        value
        disabled={disabled}
        onValueChange={(next) => onChange({ isOnline: next })}
      />
      <div className="flex items-center gap-2">
        <Selector
          aria-label={`${day.day} start`}
          options={DAY_TIMES}
          value={day.startTime}
          onChange={(startTime) => onChange({ startTime })}
          triggerClassName={TIME_FIELD_CLASS}
          contentClassName={TIME_MENU_CLASS}
          onOpenChange={scrollSelectedTime}
        />
        {startTag && <ZoneChip>{startTag}</ZoneChip>}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-body text-body-2 text-text-secondary">to</span>
        <Selector
          aria-label={`${day.day} end`}
          options={DAY_TIMES}
          value={day.endTime}
          onChange={(endTime) => onChange({ endTime })}
          triggerClassName={TIME_FIELD_CLASS}
          contentClassName={TIME_MENU_CLASS}
          onOpenChange={scrollSelectedTime}
        />
        {endTag && <ZoneChip>{endTag}</ZoneChip>}
      </div>
    </div>
  );
}
